import { Avatar, AvatarFallback, AvatarImage } from '@talentmesh/ui/components/avatar';
import { Button } from '@talentmesh/ui/components/button';
import { Input } from '@talentmesh/ui/components/input';
import { Separator } from '@talentmesh/ui/components/separator';
import { TypographyH2 } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { ChevronLeft, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';
import { useAssessmentsOverviewContext } from '../Contexts/AssessmentsOverviewContext';
import CreateRecruitmentButton from './CreateRecruitmentButton';
import { resourceBaseUrl } from '@/AppSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import useClientIdPathParam from '@/Hooks/UseClientIdPathParam';
import useCompanyId from '@/Hooks/UseCompanyId';
import useDebounce from '@/Hooks/UseDebounce';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

function AssessmentsOverviewHeader() {
    const { searchByName, search, currentTab, client } = useAssessmentsOverviewContext();
    const companyId = useCompanyId();
    const clientId = useClientIdPathParam();
    const navigate = useNavigate();
    const { currentCompanyProfile } = useCompanyContext();
    const clientIconPlaceholder = `${resourceBaseUrl}/clients/client_placeholder.svg`;
    const [searchInput, setSearchInput] = useState(search);
    const debouncedSearch = useDebounce(searchInput, 300);

    const active = generatePath(RoutePath.AssessmentsActive, { companyId, clientId });
    const closed = generatePath(RoutePath.AssessmentsClosed, { companyId, clientId });

    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    useEffect(() => {
        searchByName(debouncedSearch);
    }, [debouncedSearch, searchByName]);

    const backClickHandler = () => {
        const clientsOverviewPath = generatePath(RoutePath.CompanyRoot, { companyId });
        navigate(clientsOverviewPath);
    };

    const getCurrentClientIconURL = (clientResponse: ClientResponse): string => {
        if (clientResponse.isDefault) {
            if (currentCompanyProfile.companyIconURL !== '') {
                return currentCompanyProfile.companyIconURL;
            }
        }

        return clientResponse.iconURL ?? clientIconPlaceholder;
    };

    const tabClassName = (urlToMatch: string) =>
        cn(
            'inline-flex items-center border-b-2 border-transparent px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground',
            currentTab === urlToMatch && 'border-primary text-foreground font-semibold',
        );

    return (
        <div className="border-border bg-background border-b">
            <div className="mx-auto w-full max-w-6xl">
                <div className="mb-3 flex flex-row flex-wrap items-start justify-start gap-6 pt-8">
                    <div className="flex flex-row items-center gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={backClickHandler}
                            aria-label={UIStrings.Back}
                        >
                            <ChevronLeft className="size-5" />
                        </Button>
                        <TypographyH2 className="text-foreground">{UIStrings.RecruitmentsOverview}</TypographyH2>
                    </div>
                    <Separator orientation="vertical" className="hidden min-h-8 sm:block" />
                    <div className="flex flex-row items-center gap-2">
                        <Avatar className="size-10 rounded-full">
                            <AvatarImage
                                src={getCurrentClientIconURL(client)}
                                alt={UIStrings.CompanyNameLogo(client.name)}
                                data-cy="ClientIcon"
                            />
                            <AvatarFallback>{client.name.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                        <TypographyH2 className="text-foreground">{client.name}</TypographyH2>
                    </div>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <nav className="flex flex-row items-end gap-2" aria-label={UIStrings.RecruitmentsOverview}>
                        <NavLink to={active} className={tabClassName(active)}>
                            {UIStrings.Active}
                        </NavLink>
                        <NavLink to={closed} className={tabClassName(closed)}>
                            {UIStrings.Closed}
                        </NavLink>
                    </nav>
                    <div className="mb-3 flex w-full flex-row flex-wrap items-center justify-end gap-3 sm:w-auto">
                        <div className="relative max-w-70 flex-1">
                            <Search
                                className="text-muted-foreground absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
                                aria-hidden
                            />
                            <Input
                                placeholder={UIStrings.SearchRecruitments}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="bg-background pl-10"
                                aria-label={UIStrings.SearchRecruitments}
                            />
                        </div>
                        <CreateRecruitmentButton />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AssessmentsOverviewHeader;
